import { useState, useEffect } from 'react'
import { supabase } from './lib/supabaseClient'
import MessagingSystem from './components/MessagingSystem'
import WhatsAppButton from './components/WhatsAppButton'
import PortalLayout from './components/PortalLayout'
import { responsiveStyles } from './styles/responsiveStyles'
import { Bar, Pie, Line } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler } from 'chart.js'
import * as XLSX from 'xlsx'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler)

function MarketingPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [campaigns, setCampaigns] = useState([])
  const [promotions, setPromotions] = useState([])
  const [properties, setProperties] = useState([])
  const [leads, setLeads] = useState([])
  const [emailCampaigns, setEmailCampaigns] = useState([])
  const [scheduledPosts, setScheduledPosts] = useState([])
  const [landingPages, setLandingPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showEmailBuilder, setShowEmailBuilder] = useState(false)
  const [showScheduler, setShowScheduler] = useState(false)
  const [showLandingBuilder, setShowLandingBuilder] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [showMessages, setShowMessages] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('marketing')
  const [emailCompose, setEmailCompose] = useState({ to: '', subject: '', body: '', lead_id: null })
  const [selectedLead, setSelectedLead] = useState(null)
  const [emailTemplate, setEmailTemplate] = useState({ 
    name: '', 
    subject: '', 
    content: '', 
    template_type: 'welcome',
    scheduled_date: ''
  })
  const [scheduledPost, setScheduledPost] = useState({
    platform: '',
    content: '',
    image_url: '',
    scheduled_date: '',
    property_id: null
  })
  const [landingPage, setLandingPage] = useState({
    title: '',
    slug: '',
    content: '',
    property_id: null,
    status: 'draft'
  })
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalLeads: 0,
    totalConversions: 0,
    averageROI: 0,
    emailOpenRate: 0,
    emailClickRate: 0,
    socialReach: 0,
    socialEngagement: 0
  })
  const [analyticsData, setAnalyticsData] = useState({
    campaignPerformance: { labels: [], incomeData: [], expenseData: [] },
    leadSourceData: { labels: [], data: [] },
    engagementTrendData: { labels: [], data: [] }
  })

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'campaigns', label: 'Campaigns', icon: '📢' },
    { id: 'promotions', label: 'Social Promotions', icon: '📱' },
    { id: 'emailCampaigns', label: 'Email Marketing', icon: '📧' },
    { id: 'scheduledPosts', label: 'Social Scheduler', icon: '📅' },
    { id: 'landingPages', label: 'Landing Pages', icon: '🌐' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' },
    { id: 'emails', label: 'Emails', icon: '📧' }
  ]

  // Email templates library
  const emailTemplates = {
    welcome: {
      name: 'Welcome Email',
      subject: `Welcome to ${companyName}!`,
      content: `Dear {{lead_name}},\n\nWelcome to ${companyName}! We're excited to help you find your dream property.\n\nHere are some properties you might like:\n{{property_list}}\n\nFeel free to reply with any questions!\n\nBest regards,\nThe ${companyName} Team`
    },
    followup: {
      name: 'Property Follow-up',
      subject: 'Following up on {{property_title}}',
      content: `Dear {{lead_name}},\n\nI wanted to follow up on your interest in {{property_title}} located at {{property_address}}.\n\nWould you like to schedule a viewing? I'm available:\n- Tomorrow at 2 PM\n- Wednesday at 11 AM\n- Friday at 3 PM\n\nLet me know what works best for you!\n\nBest regards,\n{{agent_name}}`
    },
    openhouse: {
      name: 'Open House Invitation',
      subject: `You're Invited! Open House at ${companyName}`,
      content: `Dear {{lead_name}},\n\nYou're invited to an exclusive open house at {{property_address}}!\n\n📅 Date: {{open_house_date}}\n🕐 Time: {{open_house_time}}\n📍 Location: {{property_address}}\n\nRegister here: {{registration_link}}\n\nSee you there!\nThe ${companyName} Team`
    },
    newsletter: {
      name: 'Monthly Newsletter',
      subject: `${companyName} Monthly Newsletter - {{month}}`,
      content: `Dear {{lead_name}},\n\nWelcome to our monthly newsletter!\n\n🔥 HOT PROPERTIES THIS MONTH:\n{{hot_properties}}\n\n📊 MARKET UPDATE:\n{{market_update}}\n\n💡 TIPS & ADVICE:\n{{tips}}\n\nThank you for being part of our community!`
    }
  }

  // Get company ID from session
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) return

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching company_id:', error)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        return
      }

      setCompanyId(userData.company_id)
      
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .maybeSingle()
      
      if (company) {
        setCompanyName(company.name)
      }
    }

    getCompanyId()
  }, [session])

  // Fetch all data when companyId is available
  useEffect(() => {
    if (companyId) {
      fetchMarketingData()
      fetchUnreadMessages()
    }
  }, [activeTab, companyId])

  const fetchUnreadMessages = async () => {
    if (!companyId) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)
    
    setUnreadCount(count || 0)
  }

  const calculateLeadScore = (lead) => {
    let score = 0
    if (lead.email_opens > 5) score += 20
    if (lead.email_clicks > 2) score += 15
    if (lead.property_views > 10) score += 25
    if (lead.property_views > 5) score += 15
    if (lead.appointment_scheduled) score += 30
    if (lead.phone_calls > 2) score += 20
    return Math.min(score, 100)
  }

  const fetchMarketingData = async () => {
    if (!companyId) return
    setLoading(true)
    
    try {
      // Fetch campaigns
      const { data: campaignsData } = await supabase
        .from('campaigns')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      setCampaigns(campaignsData || [])
      
      // Fetch promotions
      const { data: promotionsData } = await supabase
        .from('promotions')
        .select('*, properties(title)')
        .eq('company_id', companyId)
      setPromotions(promotionsData || [])
      
      // Fetch properties
      const { data: propertiesData } = await supabase
        .from('properties')
        .select('*')
        .eq('company_id', companyId)
      setProperties(propertiesData || [])
      
      // Fetch leads
      const { data: leadsData } = await supabase
        .from('leads')
        .select('*')
        .eq('company_id', companyId)
      setLeads(leadsData || [])
      
      // Fetch email campaigns
      const { data: emailData } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('company_id', companyId)
      setEmailCampaigns(emailData || [])
      
      // Fetch scheduled posts
      const { data: scheduledData } = await supabase
        .from('scheduled_posts')
        .select('*')
        .eq('company_id', companyId)
      setScheduledPosts(scheduledData || [])
      
      // Fetch landing pages
      const { data: landingData } = await supabase
        .from('landing_pages')
        .select('*')
        .eq('company_id', companyId)
      setLandingPages(landingData || [])

      // Calculate stats
      const activeCampaigns = campaignsData?.filter(c => c.status === 'active') || []
      const totalConversions = campaignsData?.reduce((sum, c) => sum + (c.conversions || 0), 0) || 0
      const avgROI = campaignsData?.length > 0 
        ? campaignsData.reduce((sum, c) => sum + (c.roi || 0), 0) / campaignsData.length 
        : 0
      
      const totalSent = emailData?.reduce((sum, e) => sum + (e.sent_count || 0), 0) || 0
      const totalOpens = emailData?.reduce((sum, e) => sum + (e.open_count || 0), 0) || 0
      const totalClicks = emailData?.reduce((sum, e) => sum + (e.click_count || 0), 0) || 0
      const totalReach = promotionsData?.reduce((sum, p) => sum + (p.views || 0), 0) || 0
      const totalEngagement = promotionsData?.reduce((sum, p) => sum + ((p.likes || 0) + (p.shares || 0) + (p.inquiries || 0)), 0) || 0
      
      // Prepare analytics data for charts
      const campaignLabels = campaignsData?.slice(0, 6).map(c => c.name?.substring(0, 15) || 'Campaign') || []
      const campaignLeads = campaignsData?.slice(0, 6).map(c => c.leads_generated || 0) || []
      const campaignConversions = campaignsData?.slice(0, 6).map(c => c.conversions || 0) || []
      
      setAnalyticsData({
        campaignPerformance: { 
          labels: campaignLabels, 
          incomeData: campaignLeads, 
          expenseData: campaignConversions 
        },
        leadSourceData: { 
          labels: ['Social Media', 'Email', 'Google Ads', 'Referral', 'Website'], 
          data: [35, 25, 20, 15, 5] 
        },
        engagementTrendData: { 
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'], 
          data: [85, 102, 134, 156] 
        }
      })
      
      setStats({
        totalCampaigns: campaignsData?.length || 0,
        activeCampaigns: activeCampaigns.length,
        totalLeads: leadsData?.length || 0,
        totalConversions: totalConversions,
        averageROI: avgROI,
        emailOpenRate: totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : 0,
        emailClickRate: totalOpens > 0 ? ((totalClicks / totalOpens) * 100).toFixed(1) : 0,
        socialReach: totalReach,
        socialEngagement: totalEngagement
      })
    } catch (err) {
      console.error('Error fetching data:', err)
    }
    
    setLoading(false)
  }

  const sendEmail = async () => {
    if (!emailCompose.to || !emailCompose.body) {
      alert('Please fill in recipient and message')
      return
    }

    const { error } = await supabase.from('emails').insert({
      to_email: emailCompose.to,
      subject: emailCompose.subject || 'No Subject',
      body: emailCompose.body,
      from_email: session.user.email,
      direction: 'outbound',
      agent_id: session.user.id,
      lead_id: emailCompose.lead_id || null,
      company_id: companyId
    })

    if (error) {
      alert('Failed to send: ' + error.message)
    } else {
      alert('Email sent successfully!')
      setEmailCompose({ to: '', subject: '', body: '', lead_id: null })
      setShowEmailModal(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      let table = ''
      let data = { ...formData, company_id: companyId }

      if (activeTab === 'campaigns') {
        table = 'campaigns'
        data.leads_generated = parseInt(data.leads_generated) || 0
        data.conversions = parseInt(data.conversions) || 0
        data.roi = parseFloat(data.roi) || 0
        data.budget = parseFloat(data.budget) || 0
      } else if (activeTab === 'promotions') {
        table = 'promotions'
      }

      if (editingItem) {
        await supabase.from(table).update(data).eq('id', editingItem.id)
      } else {
        await supabase.from(table).insert(data)
      }

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      fetchMarketingData()
    } catch (err) {
      alert('Error: ' + err.message)
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (confirm('Are you sure?')) {
      let table = ''
      if (activeTab === 'campaigns') table = 'campaigns'
      else if (activeTab === 'promotions') table = 'promotions'
      else if (activeTab === 'emailCampaigns') table = 'email_campaigns'
      else if (activeTab === 'scheduledPosts') table = 'scheduled_posts'
      else if (activeTab === 'landingPages') table = 'landing_pages'
      
      await supabase.from(table).delete().eq('id', id)
      fetchMarketingData()
    }
  }

  const createEmailCampaign = async () => {
    if (!emailTemplate.name) {
      alert('Please enter a campaign name')
      return
    }
    if (!emailTemplate.subject) {
      alert('Please enter a subject line')
      return
    }
    if (!emailTemplate.content) {
      alert('Please enter email content')
      return
    }

    const campaignData = {
      name: emailTemplate.name,
      subject: emailTemplate.subject,
      content: emailTemplate.content,
      template_type: emailTemplate.template_type,
      status: 'draft',
      created_by: session.user.id,
      company_id: companyId,
      scheduled_date: emailTemplate.scheduled_date || null,
      sent_count: 0,
      open_count: 0,
      click_count: 0
    }
    
    const { error } = await supabase.from('email_campaigns').insert(campaignData)
    if (!error) {
      alert('Email campaign created successfully!')
      setShowEmailBuilder(false)
      setEmailTemplate({ name: '', subject: '', content: '', template_type: 'welcome', scheduled_date: '' })
      fetchMarketingData()
    } else {
      alert('Error: ' + error.message)
    }
  }

  const createScheduledPost = async () => {
    if (!scheduledPost.platform) {
      alert('Please select a platform')
      return
    }
    if (!scheduledPost.content) {
      alert('Please enter post content')
      return
    }
    if (!scheduledPost.scheduled_date) {
      alert('Please select a date and time')
      return
    }

    const postData = {
      platform: scheduledPost.platform,
      content: scheduledPost.content,
      image_url: scheduledPost.image_url || null,
      scheduled_date: scheduledPost.scheduled_date,
      property_id: scheduledPost.property_id || null,
      status: 'scheduled',
      created_by: session.user.id,
      company_id: companyId,
      created_at: new Date()
    }
    
    const { error } = await supabase.from('scheduled_posts').insert(postData)
    if (!error) {
      alert('Post scheduled successfully!')
      setShowScheduler(false)
      setScheduledPost({ platform: '', content: '', image_url: '', scheduled_date: '', property_id: null })
      fetchMarketingData()
    } else {
      alert('Error: ' + error.message)
    }
  }

  const createLandingPage = async () => {
    if (!landingPage.title) {
      alert('Please enter a page title')
      return
    }
    if (!landingPage.slug) {
      alert('Please enter a slug/URL')
      return
    }

    const pageData = {
      title: landingPage.title,
      slug: landingPage.slug.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      content: landingPage.content || '',
      property_id: landingPage.property_id || null,
      status: landingPage.status || 'draft',
      created_by: session.user.id,
      company_id: companyId,
      views: 0,
      conversions: 0,
      created_at: new Date()
    }
    
    const { error } = await supabase.from('landing_pages').insert(pageData)
    if (!error) {
      alert('Landing page created successfully!')
      setShowLandingBuilder(false)
      setLandingPage({ title: '', slug: '', content: '', property_id: null, status: 'draft' })
      fetchMarketingData()
    } else {
      alert('Error: ' + error.message)
    }
  }

  const sendTestEmail = async () => {
    alert(`Test email would be sent to ${session.user.email}\n\nSubject: ${emailTemplate.subject}\n\nContent preview:\n${emailTemplate.content.substring(0, 200)}...`)
  }

  const incrementMetric = async (promotionId, metric) => {
    const promotion = promotions.find(p => p.id === promotionId)
    const newValue = (promotion[metric] || 0) + 1
    await supabase.from('promotions').update({ [metric]: newValue }).eq('id', promotionId)
    fetchMarketingData()
  }

  // Chart data for dashboard
  const campaignPerformanceData = {
    labels: analyticsData.campaignPerformance.labels,
    datasets: [
      {
        label: 'Leads Generated',
        data: analyticsData.campaignPerformance.incomeData,
        backgroundColor: 'rgba(5, 150, 105, 0.6)',
        borderColor: '#059669',
        borderWidth: 1
      },
      {
        label: 'Conversions',
        data: analyticsData.campaignPerformance.expenseData,
        backgroundColor: 'rgba(30, 58, 95, 0.6)',
        borderColor: '#1e3a5f',
        borderWidth: 1
      }
    ]
  }

  const roiData = {
    labels: campaigns.slice(0, 6).map(c => c.name?.substring(0, 15) || 'Campaign'),
    datasets: [{
      label: 'ROI %',
      data: campaigns.slice(0, 6).map(c => c.roi || 0),
      backgroundColor: 'rgba(220, 38, 38, 0.6)',
      borderColor: '#dc2626',
      borderWidth: 1
    }]
  }

  const leadSourceData = {
    labels: analyticsData.leadSourceData.labels,
    datasets: [{
      data: analyticsData.leadSourceData.data,
      backgroundColor: ['#059669', '#2c7da0', '#1e3a5f', '#f59e0b', '#8b5cf6']
    }]
  }

  const engagementTrendData = {
    labels: analyticsData.engagementTrendData.labels,
    datasets: [
      {
        label: 'Email Opens',
        data: [120, 145, 168, 192],
        borderColor: '#059669',
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Social Engagement',
        data: analyticsData.engagementTrendData.data,
        borderColor: '#2c7da0',
        backgroundColor: 'rgba(44, 125, 160, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  }

  const renderForm = () => {
    const inputStyle = { width: '100%', padding: 12, margin: '8px 0', border: '1px solid #cbd5e1', borderRadius: 12, fontSize: 14, fontFamily: 'inherit' }
    
    if (activeTab === 'campaigns') {
      return (
        <>
          <input placeholder="Campaign Name" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required style={inputStyle} />
          <select value={formData.type || ''} onChange={e => setFormData({...formData, type: e.target.value})} required style={inputStyle}>
            <option value="">Select Type</option>
            <option value="social">📱 Social Media</option>
            <option value="email">📧 Email Marketing</option>
            <option value="google">🔍 Google Ads</option>
            <option value="referral">🤝 Referral Program</option>
            <option value="other">📢 Other</option>
          </select>
          <input placeholder="Budget" type="number" value={formData.budget || ''} onChange={e => setFormData({...formData, budget: parseFloat(e.target.value)})} style={inputStyle} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={inputStyle}>
            <option value="USD">USD</option><option value="EUR">EUR</option><option value="NGN">NGN</option>
          </select>
          <input type="date" placeholder="Start Date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} style={inputStyle} />
          <input type="date" placeholder="End Date" value={formData.end_date || ''} onChange={e => setFormData({...formData, end_date: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'draft'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
          <input placeholder="Leads Generated" type="number" value={formData.leads_generated || ''} onChange={e => setFormData({...formData, leads_generated: parseInt(e.target.value) || 0})} style={inputStyle} />
          <input placeholder="Conversions" type="number" value={formData.conversions || ''} onChange={e => setFormData({...formData, conversions: parseInt(e.target.value) || 0})} style={inputStyle} />
          <input placeholder="ROI %" type="number" value={formData.roi || ''} onChange={e => setFormData({...formData, roi: parseFloat(e.target.value) || 0})} style={inputStyle} />
          <textarea placeholder="Notes" rows="2" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    if (activeTab === 'promotions') {
      return (
        <>
          <select value={formData.property_id || ''} onChange={e => setFormData({...formData, property_id: e.target.value})} required style={inputStyle}>
            <option value="">Select Property</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={formData.platform || ''} onChange={e => setFormData({...formData, platform: e.target.value})} required style={inputStyle}>
            <option value="">Select Platform</option>
            <option value="instagram">📸 Instagram</option>
            <option value="facebook">📘 Facebook</option>
            <option value="twitter">🐦 Twitter</option>
            <option value="linkedin">🔗 LinkedIn</option>
            <option value="whatsapp">💬 WhatsApp</option>
          </select>
          <input placeholder="Post URL" value={formData.post_url || ''} onChange={e => setFormData({...formData, post_url: e.target.value})} style={inputStyle} />
        </>
      )
    }
    return null
  }

  const renderList = () => {
    if (loading) return <p>Loading...</p>
    
    const cardStyle = { background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }
    const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, marginRight: 10, cursor: 'pointer', fontSize: 13 }
    const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
    const metricsGrid = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 15 }
    const metricBox = { background: '#f8fafc', padding: '10px', textAlign: 'center', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', fontSize: 18, fontWeight: 'bold', display: 'flex', flexDirection: 'column', alignItems: 'center' }
    
    if (activeTab === 'campaigns') {
      if (campaigns.length === 0) return <p>No campaigns yet. Click "+ Add New" to create your first campaign!</p>
      return (
        <div style={responsiveStyles.grid}>
          {campaigns.map(c => (
            <div key={c.id} style={cardStyle}>
              <h3>📢 {c.name}</h3>
              <p>Type: {c.type} | Status: <span style={{background: c.status === 'active' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{c.status}</span></p>
              <p>Budget: {c.currency} {c.budget?.toLocaleString()}</p>
              <p>📅 {c.start_date ? new Date(c.start_date).toLocaleDateString() : 'Not set'} - {c.end_date ? new Date(c.end_date).toLocaleDateString() : 'Not set'}</p>
              <p>🎯 Leads: {c.leads_generated || 0} | ✅ Conversions: {c.conversions || 0}</p>
              <p>📈 ROI: {c.roi || 0}%</p>
              <div style={{display: 'flex', gap: 10, marginTop: 10}}>
                <button onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(c.id)} style={deleteBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    if (activeTab === 'promotions') {
      if (promotions.length === 0) return <p>No promotions yet. Click "+ Add New" to create one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {promotions.map(p => {
            const property = properties.find(prop => prop.id === p.property_id)
            return (
              <div key={p.id} style={cardStyle}>
                <h3>🏠 {property?.title || 'Unknown Property'}</h3>
                <p>Platform: {p.platform}</p>
                {p.post_url && <p>🔗 <a href={p.post_url} target="_blank" rel="noopener noreferrer" style={{color: '#2c7da0'}}>View Post</a></p>}
                <div style={metricsGrid}>
                  <div onClick={() => incrementMetric(p.id, 'views')} style={metricBox}>👁️ {p.views || 0}<small>Views</small></div>
                  <div onClick={() => incrementMetric(p.id, 'likes')} style={metricBox}>❤️ {p.likes || 0}<small>Likes</small></div>
                  <div onClick={() => incrementMetric(p.id, 'shares')} style={metricBox}>🔄 {p.shares || 0}<small>Shares</small></div>
                  <div onClick={() => incrementMetric(p.id, 'inquiries')} style={metricBox}>💬 {p.inquiries || 0}<small>Inquiries</small></div>
                </div>
                <div style={{display: 'flex', gap: 8, marginTop: 10}}>
                  <WhatsAppButton type="promotion" data={p} phoneNumber="" session={session} companyName={companyName} buttonText="📱 Share on WhatsApp" />
                  <button onClick={() => handleDelete(p.id)} style={{...deleteBtn, marginTop: 10, width: '100%'}}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    if (activeTab === 'emailCampaigns') {
      if (emailCampaigns.length === 0) return <p>No email campaigns yet. Click "Create Email Campaign" to get started.</p>
      return (
        <div style={responsiveStyles.grid}>
          {emailCampaigns.map(e => (
            <div key={e.id} style={cardStyle}>
              <h3>📧 {e.name}</h3>
              <p>Subject: {e.subject}</p>
              <p>Template: {e.template_type}</p>
              <p>Status: <span style={{background: e.status === 'sent' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{e.status}</span></p>
              <p>📊 Sent: {e.sent_count || 0} | Opens: {e.open_count || 0} | Clicks: {e.click_count || 0}</p>
              {e.scheduled_date && <p>📅 Scheduled: {new Date(e.scheduled_date).toLocaleDateString()}</p>}
              <div style={{display: 'flex', gap: 10, marginTop: 10}}>
                <button onClick={() => handleDelete(e.id)} style={deleteBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'scheduledPosts') {
      if (scheduledPosts.length === 0) return <p>No scheduled posts. Click "Schedule Post" to get started.</p>
      return (
        <div style={responsiveStyles.grid}>
          {scheduledPosts.map(p => (
            <div key={p.id} style={cardStyle}>
              <h3>{p.platform} 📱</h3>
              <p>{p.content?.substring(0, 100)}...</p>
              <p>📅 Scheduled: {new Date(p.scheduled_date).toLocaleString()}</p>
              <p>Status: <span style={{background: p.status === 'published' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{p.status}</span></p>
              <div style={{display: 'flex', gap: 10, marginTop: 10}}>
                <WhatsAppButton type="scheduled_post" data={p} phoneNumber="" session={session} companyName={companyName} buttonText="📱 Share Post" />
                <button onClick={() => handleDelete(p.id)} style={deleteBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'landingPages') {
      if (landingPages.length === 0) return <p>No landing pages yet. Click "Create Landing Page" to get started.</p>
      return (
        <div style={responsiveStyles.grid}>
          {landingPages.map(l => {
            const property = properties.find(p => p.id === l.property_id)
            return (
              <div key={l.id} style={cardStyle}>
                <h3>🌐 {l.title}</h3>
                <p>Slug: /{l.slug}</p>
                <p>Property: {property?.title || 'General'}</p>
                <p>Status: <span style={{background: l.status === 'published' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{l.status}</span></p>
                <p>📊 Views: {l.views || 0} | Conversions: {l.conversions || 0}</p>
                <div style={{display: 'flex', gap: 10, marginTop: 10}}>
                  <WhatsAppButton type="landing_page" data={l} phoneNumber="" session={session} companyName={companyName} buttonText="📱 Share Page" />
                  <button onClick={() => handleDelete(l.id)} style={deleteBtn}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    
    return null
  }

  // Styles
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const statCard = { background: '#fff', padding: 20, borderRadius: 20, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }
  const statNumber = { fontSize: 36, fontWeight: 700, color: '#1e3a5f', margin: '10px 0 0' }
  const chartCard = { background: '#fff', borderRadius: 20, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
  const recentCard = { background: '#fff', padding: 20, borderRadius: 20, border: '1px solid #e2e8f0', marginBottom: 30 }
  const leadScoreItem = { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 40, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  const actionButtonStyle = { background: '#059669', color: '#fff', border: 'none', padding: '12px 18px', borderRadius: 10, cursor: 'pointer', fontSize: 14, textAlign: 'left', fontWeight: 500, marginTop: 8 }
  const inputStyle = { width: '100%', padding: 12, margin: '8px 0', border: '1px solid #cbd5e1', borderRadius: 12, fontSize: 14, fontFamily: 'inherit' }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: 25, borderRadius: 20, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, cursor: 'pointer', fontWeight: 600 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, cursor: 'pointer' }
  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }

  if (loading && !companyId) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading company data...</div>
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Marketing Portal"
      portalIcon="📢"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlayStyle} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div style={modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2>📧 {selectedLead ? `Send Email to ${selectedLead.client_name}` : 'Compose Email'}</h2>
            <input 
              placeholder="To" 
              value={emailCompose.to} 
              onChange={e => setEmailCompose({...emailCompose, to: e.target.value})} 
              style={inputStyle} 
            />
            <input 
              placeholder="Subject" 
              value={emailCompose.subject} 
              onChange={e => setEmailCompose({...emailCompose, subject: e.target.value})} 
              style={inputStyle} 
            />
            <textarea 
              placeholder="Message" 
              rows="8" 
              value={emailCompose.body} 
              onChange={e => setEmailCompose({...emailCompose, body: e.target.value})} 
              style={inputStyle} 
            />
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={sendEmail} style={saveBtn}>Send Email</button>
              <button onClick={() => setShowEmailModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1>{activeTab === 'dashboard' ? 'Marketing Dashboard' : activeTab === 'emailCampaigns' ? 'Email Marketing' : activeTab === 'scheduledPosts' ? 'Social Media Scheduler' : activeTab === 'landingPages' ? 'Landing Pages' : activeTab === 'whatsapp' ? 'WhatsApp Marketing' : activeTab === 'emails' ? 'Email Communication' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          <p>Welcome, {session.user.email} - {companyName} Marketing</p>
        </div>
        {activeTab !== 'dashboard' && activeTab !== 'emailCampaigns' && activeTab !== 'scheduledPosts' && activeTab !== 'landingPages' && activeTab !== 'whatsapp' && activeTab !== 'emails' && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>
            + Add New
          </button>
        )}
      </div>

      {/* Action Buttons for specific tabs */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {activeTab === 'emailCampaigns' && (
          <button onClick={() => setShowEmailBuilder(true)} style={actionButtonStyle}>✏️ Create Email Campaign</button>
        )}
        {activeTab === 'scheduledPosts' && (
          <button onClick={() => setShowScheduler(true)} style={actionButtonStyle}>📅 Schedule Post</button>
        )}
        {activeTab === 'landingPages' && (
          <button onClick={() => setShowLandingBuilder(true)} style={actionButtonStyle}>🌐 Create Landing Page</button>
        )}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          <div style={responsiveStyles.statsGrid}>
            <div style={statCard}><h3>📢 Campaigns</h3><p style={statNumber}>{stats.totalCampaigns}</p><small>Total</small></div>
            <div style={statCard}><h3>✅ Active</h3><p style={statNumber}>{stats.activeCampaigns}</p><small>Live now</small></div>
            <div style={statCard}><h3>🎯 Leads</h3><p style={statNumber}>{stats.totalLeads}</p><small>Generated</small></div>
            <div style={statCard}><h3>📈 ROI</h3><p style={statNumber}>{stats.averageROI}%</p><small>Average</small></div>
            <div style={statCard}><h3>📧 Email Open</h3><p style={statNumber}>{stats.emailOpenRate}%</p><small>Rate</small></div>
            <div style={statCard}><h3>📱 Social Reach</h3><p style={statNumber}>{stats.socialReach.toLocaleString()}</p><small>Impressions</small></div>
          </div>

          <div style={responsiveStyles.chartsGrid}>
            <div style={chartCard}>
              <h3>Campaign Performance</h3>
              <div style={{height: 300}}>
                <Bar data={campaignPerformanceData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3>ROI by Campaign</h3>
              <div style={{height: 300}}>
                <Bar data={roiData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3>Lead Sources</h3>
              <div style={{height: 300}}>
                <Pie data={leadSourceData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3>Engagement Trends</h3>
              <div style={{height: 300}}>
                <Line data={engagementTrendData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>

          <div style={recentCard}>
            <h3>🎯 Lead Scoring - Hot Leads</h3>
            {leads.filter(l => calculateLeadScore(l) >= 70).slice(0, 5).map(lead => (
              <div key={lead.id} style={leadScoreItem}>
                <span><strong>{lead.client_name}</strong> - Score: {calculateLeadScore(lead)}</span>
                <span style={{color: '#059669'}}>🔥 Hot Lead</span>
              </div>
            ))}
            {leads.filter(l => calculateLeadScore(l) >= 70).length === 0 && <p>No hot leads yet. Engage more with your leads!</p>}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ marginTop: 20 }}>
        {renderList()}
      </div>

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div>
          <h2>📱 WhatsApp Marketing</h2>
          <div style={responsiveStyles.grid}>
            <div style={cardStyle}>
              <h3>📢 Share Campaign</h3>
              <p>Send campaign details via WhatsApp</p>
              {campaigns.filter(c => c.status === 'active').slice(0, 5).map(campaign => (
                <div key={campaign.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{campaign.name}</strong> - {campaign.type}</p>
                  <WhatsAppButton type="campaign" data={campaign} phoneNumber="" session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3>📱 Share Promotion</h3>
              <p>Send social promotions via WhatsApp</p>
              {promotions.slice(0, 5).map(promo => (
                <div key={promo.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{promo.properties?.title}</strong> - {promo.platform}</p>
                  <WhatsAppButton type="promotion" data={promo} phoneNumber="" session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3>🌐 Share Landing Page</h3>
              <p>Send landing pages via WhatsApp</p>
              {landingPages.filter(l => l.status === 'published').slice(0, 5).map(page => (
                <div key={page.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{page.title}</strong> - /{page.slug}</p>
                  <WhatsAppButton type="landing_page" data={page} phoneNumber="" session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3>📢 Broadcast Templates</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <WhatsAppButton 
                  type="bulk" 
                  data={{ message: `🏠 New properties available at ${companyName}! Check out our latest listings.` }} 
                  phoneNumber="" 
                  session={session} 
                  companyName={companyName}
                  buttonText="🏠 Property Update"
                />
                <WhatsAppButton 
                  type="bulk" 
                  data={{ message: `📅 Reminder: You have an upcoming appointment with ${companyName}. Please confirm your attendance.` }} 
                  phoneNumber="" 
                  session={session} 
                  companyName={companyName}
                  buttonText="📅 Appointment Reminder"
                />
                <WhatsAppButton 
                  type="bulk" 
                  data={{ message: `⭐ We value your feedback! Please rate your experience with ${companyName}.` }} 
                  phoneNumber="" 
                  session={session} 
                  companyName={companyName}
                  buttonText="⭐ Feedback Request"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emails Tab */}
      {activeTab === 'emails' && (
        <div>
          <h2>📧 Email Communication</h2>
          <div style={responsiveStyles.grid}>
            <div style={cardStyle}>
              <h3>✏️ Compose New Email</h3>
              <p>Send emails to leads or contacts</p>
              <button 
                onClick={() => {
                  setSelectedLead(null)
                  setEmailCompose({ to: '', subject: '', body: '', lead_id: null })
                  setShowEmailModal(true)
                }} 
                style={addBtn}
              >
                📧 Compose Email
              </button>
            </div>

            <div style={cardStyle}>
              <h3>🎯 Quick Email to Leads</h3>
              <p>Send emails to your leads</p>
              {leads.slice(0, 5).map(lead => (
                <div key={lead.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{lead.client_name}</strong> - Score: {calculateLeadScore(lead)}</p>
                  <button 
                    onClick={() => {
                      setSelectedLead(lead)
                      setEmailCompose({ 
                        to: lead.client_email, 
                        subject: '', 
                        body: '', 
                        lead_id: lead.id 
                      })
                      setShowEmailModal(true)
                    }} 
                    style={{background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer'}}
                  >
                    📧 Send Email
                  </button>
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3>📝 Email Templates</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Special Property Offer from ${companyName}!`,
                      body: `Dear Lead,\n\nWe have an exciting property that matches your criteria!\n\n🏠 Property: [Property Name]\n📍 Location: [City, Country]\n💰 Price: [Price]\n\nWould you like to schedule a viewing?\n\nBest regards,\n${companyName} Team`,
                      lead_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#1e3a5f', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%'}}
                >
                  📝 Property Offer Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Follow-up on Your Property Interest - ${companyName}`,
                      body: `Dear Lead,\n\nI wanted to follow up on your recent property inquiry.\n\nDo you have any questions I can answer?\n\nWould you like to schedule a call or viewing?\n\nLooking forward to hearing from you!\n\nBest regards,\n${companyName} Team`,
                      lead_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#1e3a5f', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%'}}
                >
                  📝 Follow-up Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Open House Invitation - ${companyName}`,
                      body: `Dear Lead,\n\nYou're invited to our open house event!\n\n📅 Date: [Date]\n🕐 Time: [Time]\n📍 Location: [Address]\n\nRegister here: [Link]\n\nSee you there!\n\nBest regards,\n${companyName} Team`,
                      lead_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#f59e0b', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%'}}
                >
                  📝 Open House Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? 'Edit' : 'Add New'} {activeTab.slice(0, -1)}</h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{display: 'flex', gap: 10, marginTop: 20}}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Builder Modal */}
      {showEmailBuilder && (
        <div style={modalOverlay} onClick={() => setShowEmailBuilder(false)}>
          <div style={{...modalStyle, maxWidth: 600}} onClick={(e) => e.stopPropagation()}>
            <h2>✏️ Create Email Campaign - {companyName}</h2>
            <select value={emailTemplate.template_type} onChange={e => {
              const template = emailTemplates[e.target.value]
              setEmailTemplate({
                ...emailTemplate,
                template_type: e.target.value,
                name: template.name,
                subject: template.subject,
                content: template.content
              })
            }} style={inputStyle}>
              <option value="welcome">Welcome Email</option>
              <option value="followup">Property Follow-up</option>
              <option value="openhouse">Open House Invitation</option>
              <option value="newsletter">Monthly Newsletter</option>
            </select>
            <input placeholder="Campaign Name" value={emailTemplate.name} onChange={e => setEmailTemplate({...emailTemplate, name: e.target.value})} style={inputStyle} />
            <input placeholder="Subject Line" value={emailTemplate.subject} onChange={e => setEmailTemplate({...emailTemplate, subject: e.target.value})} style={inputStyle} />
            <textarea placeholder="Email Content" rows="8" value={emailTemplate.content} onChange={e => setEmailTemplate({...emailTemplate, content: e.target.value})} style={inputStyle} />
            <input type="datetime-local" placeholder="Schedule Date (optional)" value={emailTemplate.scheduled_date} onChange={e => setEmailTemplate({...emailTemplate, scheduled_date: e.target.value})} style={inputStyle} />
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={sendTestEmail} style={{...editBtn, background: '#f59e0b'}}>Send Test</button>
              <button onClick={createEmailCampaign} style={saveBtn}>Save Campaign</button>
              <button onClick={() => setShowEmailBuilder(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Scheduler Modal */}
      {showScheduler && (
        <div style={modalOverlay} onClick={() => setShowScheduler(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2>📅 Schedule Social Media Post - {companyName}</h2>
            <select value={scheduledPost.platform} onChange={e => setScheduledPost({...scheduledPost, platform: e.target.value})} style={inputStyle}>
              <option value="">Select Platform</option>
              <option value="instagram">📸 Instagram</option>
              <option value="facebook">📘 Facebook</option>
              <option value="twitter">🐦 Twitter</option>
              <option value="linkedin">🔗 LinkedIn</option>
            </select>
            <select value={scheduledPost.property_id || ''} onChange={e => setScheduledPost({...scheduledPost, property_id: e.target.value || null})} style={inputStyle}>
              <option value="">Select Property (optional)</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <textarea placeholder="Post Content" rows="4" value={scheduledPost.content} onChange={e => setScheduledPost({...scheduledPost, content: e.target.value})} style={inputStyle} />
            <input placeholder="Image URL (optional)" value={scheduledPost.image_url} onChange={e => setScheduledPost({...scheduledPost, image_url: e.target.value})} style={inputStyle} />
            <input type="datetime-local" placeholder="Schedule Date & Time" value={scheduledPost.scheduled_date} onChange={e => setScheduledPost({...scheduledPost, scheduled_date: e.target.value})} required style={inputStyle} />
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={createScheduledPost} style={saveBtn}>Schedule Post</button>
              <button onClick={() => setShowScheduler(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Landing Page Builder Modal */}
      {showLandingBuilder && (
        <div style={modalOverlay} onClick={() => setShowLandingBuilder(false)}>
          <div style={{...modalStyle, maxWidth: 600}} onClick={(e) => e.stopPropagation()}>
            <h2>🌐 Create Landing Page - {companyName}</h2>
            <input placeholder="Page Title" value={landingPage.title} onChange={e => setLandingPage({...landingPage, title: e.target.value})} style={inputStyle} />
            <input placeholder="Slug (URL)" value={landingPage.slug} onChange={e => setLandingPage({...landingPage, slug: e.target.value})} style={inputStyle} />
            <select value={landingPage.property_id || ''} onChange={e => setLandingPage({...landingPage, property_id: e.target.value || null})} style={inputStyle}>
              <option value="">Select Property (optional)</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <textarea placeholder="Page Content (HTML supported)" rows="10" value={landingPage.content} onChange={e => setLandingPage({...landingPage, content: e.target.value})} style={inputStyle} />
            <select value={landingPage.status} onChange={e => setLandingPage({...landingPage, status: e.target.value})} style={inputStyle}>
              <option value="draft">Draft</option>
              <option value="published">Publish</option>
            </select>
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={createLandingPage} style={saveBtn}>Create Page</button>
              <button onClick={() => setShowLandingBuilder(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default MarketingPortal